import { defineStore } from 'pinia'
import { PageName, StorageKey } from '@/models/enums'
import { router } from '@/router'
import { LoginResponse, UserInfo, UserTokens } from '@/models/login'
import { ensureAMapLoaded } from '@/utils/amap-loader'
import LoginToast from '@/components/common/popup/toast.vue'
import { i18n } from '@/i18n'
import { apiConfig } from '@/config/api'

const { t: $t } = i18n.global
const { modeName } = apiConfig
const STORE_KEY = 'user'

export const useUserStore = defineStore({
  id: STORE_KEY,
  state: () => {
    // 从 localStorage 读取 USER_TOKENS，如果存在则使用，否则使用默认值
    const storedTokens = storage.getItem(StorageKey.USER_TOKENS) as UserTokens | undefined
    const defaultUserTokens: UserTokens = {
      mpOpenid: '',
      access_token: '',
      expires_at: '',
      userId: '',
      nickname: '',
      avatar: '',
      phone_number: '',
      phone_number_prefix: '',
      client_id: '',
      gender: 2,
      signature: '',
    }

    return {
      userInfo: {
        wechatCode: '',
        userTokens: storedTokens || defaultUserTokens,
      },
      userLocation: [113.554735, 22.187497] as [number, number],
      // 记录是否成功获取用户位置
      hasUserLocation: false,
      center: [113.554735, 22.187497] as [number, number],
      currentRouteName: '' as string | PageName,
      pageNameBeforeLogin: '' as string | PageName,
      loginToReservation: false,
    }
  },
  getters: {
    existingUserToken: (state) => {
      return state.userInfo.userTokens.access_token
    },
    existingUserOpenId: (state) => {
      return state.userInfo.userTokens.mpOpenid
    },
    existingUserId: (state) => {
      return state.userInfo.userTokens.userId
    },
    existingNickname: (state) => {
      const nickname = state.userInfo.userTokens.nickname
      if (nickname) return nickname
      const gStore = useGlobalStore()
      if (gStore.inMPWeixin || gStore.inWeixin) return $t('profilePage.wechatUser')
      if (gStore.inMPAli) return $t('profilePage.aliUser')
      if (gStore.inMPToutiao) return $t('profilePage.toutiaoUser')
      if (gStore.isOtherBrowser) return $t('profilePage.anonymousUser')
      return $t('profilePage.anonymousUser')
    },
    existingAvatar: (state) => {
      return state.userInfo.userTokens.avatar
    },
    existingPhoneNumber: (state) => {
      return state.userInfo.userTokens.phone_number
    },
    existingGender: (state) => {
      return state.userInfo.userTokens.gender != 2
    },
    checkIsLogin: (state) => {
      const userTokens = storage.getItem(StorageKey.USER_TOKENS) as UserTokens
      if (state.userInfo.userTokens.access_token || (userTokens && userTokens.access_token)) {
        return true
      } else {
        return false
      }
    },
    hasIncompleteInfo: (state) => {
      return (
        !state.userInfo.userTokens.nickname ||
        !state.userInfo.userTokens.avatar ||
        !state.userInfo.userTokens.phone_number ||
        state.userInfo.userTokens.gender === 2
      )
    },
    maskedPhoneNumber: (state) => {
      const storedTokens = storage.getItem(StorageKey.USER_TOKENS) as UserTokens
      const phoneNumber = state.userInfo.userTokens?.phone_number || storedTokens?.phone_number

      if (phoneNumber) {
        const phoneStr = phoneNumber.toString()
        if (phoneStr.length > 10) {
          return phoneStr.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
        } else {
          return phoneStr.replace(/(\d{2})\d{3}(\d{3})/, '$1***$2')
        }
      }

      // return $t('profilePage.phoneNumberText')
      return ''
    },
  },
  actions: {
    setUserLocation(userPosition: [number, number]) {
      this.hasUserLocation = true
      this.userLocation = userPosition
    },
    setCenter(center: [number, number]) {
      this.center = center
    },
    setUserInformation(user_info: UserInfo) {
      const { nickname, avatar, gender, phone_number, signature } = user_info
      this.userInfo.userTokens.nickname = nickname || ''
      this.userInfo.userTokens.avatar = avatar ? avatar : ''
      this.userInfo.userTokens.phone_number = phone_number ? phone_number : ''
      this.userInfo.userTokens.gender = gender === 0 ? 0 : gender === 1 ? 1 : 2
      this.userInfo.userTokens.signature = signature || ''
    },
    setUserTokens() {
      const userTokens = storage.getItem(StorageKey.USER_TOKENS) as UserTokens
      if (userTokens) {
        this.userInfo.userTokens = userTokens
      } else {
        this.logout()
        router.push({ name: PageName.LOGIN })
      }
    },
    saveUserTokens(user_tokens: UserTokens) {
      const {
        access_token,
        mpOpenid,
        expires_at,
        client_id,
        nickname,
        userId,
        avatar,
        phone_number,
      } = user_tokens
      this.userInfo.userTokens.access_token = access_token
      this.userInfo.userTokens.mpOpenid = mpOpenid
      this.userInfo.userTokens.userId = userId
      this.userInfo.userTokens.expires_at = expires_at
      this.userInfo.userTokens.client_id = client_id
      this.userInfo.userTokens.phone_number = phone_number
      this.userInfo.userTokens.nickname = nickname
      this.userInfo.userTokens.avatar = avatar
    },
    storageUserTokens() {
      storage.setItem(StorageKey.USER_TOKENS, this.userInfo.userTokens)
    },
    checkTokenIsExpired(): boolean {
      const userTokens =
        storage.getItem(StorageKey.USER_TOKENS) || (this.userInfo.userTokens as UserTokens)
      if (userTokens && userTokens.expires_at) {
        const { expires_at } = userTokens
        const now = new Date().getTime()
        const exp = new Date(expires_at).getTime()
        return now > exp
      } else {
        return true
      }
    },
    clearWechatCode() {
      this.userInfo.wechatCode = ''
    },
    queryAndSetUserProfile(focus?: boolean) {
      if (!this.checkTokenIsExpired()) {
        if (this.hasIncompleteInfo || focus) {
          queryUserProfie()
            .then((data) => {
              const { nickName, avatar, phoneNumber, gender, signature } = data
              this.setUserInformation({
                avatar,
                gender,
                phone_number: phoneNumber,
                nickname: nickName,
                signature,
              })
              const pageSpy = usePageSpy()
              pageSpy.updateRoomInfo({
                title: `[${modeName}] ${nickName}(${phoneNumber})`,
              })
            })
            .catch((error) => {
              console.error('个人信息页面获取用户信息失败', error)
            })
        }
      }
    },
    logout() {
      storage.removeStorage()
      this.$reset()

      const gStore = useGlobalStore()

      // 通知小程序移除登錄信息
      if (gStore.inMP) {
        window.uni.navigateTo({
          url: '/pages-other/actions/index?action=logout',
        })
      } else if (gStore.inWeixin) {
        const url = window.location.href
        window.location.href = gStore.getWeixinRedirectUrl(url)
      } else if (gStore.isOtherBrowser) {
        router.push({ name: PageName.HOME })
      }
    },
    toUserInfo() {
      this.checkLoginAndExecute(() => {
        router.push({ name: PageName.PERSONAL })
      }, true)
    },
    async getUserGeolocation() {
      await ensureAMapLoaded()

      // 动态 import。注意：组件库现在已经回到首屏的静态依赖里了
      //（原因见 utils/amap-loader.ts 顶部），所以这里已经省不到字节 ——
      // 留着只是因为它命中模块缓存、零成本。
      const { useGeolocation } = await import('@vuemap/vue-amap')

      const { getCurrentPosition } = await useGeolocation({
        enableHighAccuracy: true,
        needAddress: true,
      })

      try {
        const currentPosition = await getCurrentPosition()
        const lnglatArray = currentPosition.position.toArray()
        this.userLocation = lnglatArray
        this.center = lnglatArray
      } catch (error) {
        console.error('获取用户经纬度失败', error)
      }
    },
    /**
     * 通用的登录检查函数
     * @param callback 回调函数，如果已登录则执行
     * @returns boolean 是否已登录
     */
    checkLoginAndExecute(callback: () => void, toLoginPage: boolean = false): boolean {
      if (!this.checkIsLogin) {
        if (toLoginPage) {
          router.push({ name: PageName.LOGIN })
        } else {
          usePopup({
            component: LoginToast,
            props: {
              icon: 'login',
              content: $t('profilePage.loginPopup.content'),
              buttonText: $t('buttonText.toLogin'),
            },
          })
            .then(() => {
              router.push({ name: PageName.LOGIN })
            })
            .catch(() => {})
        }
        return false
      }
      callback()
      return true
    },
  },
})
